<template>
    <div class="pacman-container">
        <div id="pacman"></div>

        <p v-if="!isTouchDevice"><span>N - New Game</span><span>P - Pause</span><span>Arrow Keys - Move</span></p>
        <p v-else>Tap the maze to start<br>Swipe to move</p>
    </div>
</template>

<script setup>
    const isTouchDevice = computed(() => {
        if (!process.client) return false
        return ('ontouchstart' in window) || (navigator.maxTouchPoints ?? 0) > 0
    })

    let NONE        = 4,
        UP          = 3,
        LEFT        = 2,
        DOWN        = 1,
        RIGHT       = 11,
        WAITING     = 5,
        PAUSE       = 6,
        PLAYING     = 7,
        COUNTDOWN   = 8,
        EATEN_PAUSE = 9,
        DYING       = 10,
        Pacman      = {};

    Pacman.FPS = 30;

    // Extra space above the maze for dialog messages (canvas is taller; ctx is translated down)
    Pacman.TOP_PADDING = 50;

    // const DEBUG_DISABLE_GHOSTS = import.meta.dev;
    const DEBUG_DISABLE_GHOSTS = false;

    Pacman.Ghost = function (game, map, colour) {

        var position  = null,
            direction = null,
            eatable   = null,
            eaten     = null,
            due       = null;
        
        function getNewCoord(dir, current) { 
            
            var speed  = isVunerable() ? 1 : isHidden() ? 4 : 2,
                xSpeed = (dir === LEFT && -speed || dir === RIGHT && speed || 0),
                ySpeed = (dir === DOWN && speed || dir === UP && -speed || 0);
        
            return {
                "x": addBounded(current.x, xSpeed),
                "y": addBounded(current.y, ySpeed)
            };
        };

        /* Collision detection(walls) is done when a ghost lands on an
        * exact block, make sure they dont skip over it 
        */
        function addBounded(x1, x2) { 
            var rem    = x1 % 10, 
                result = rem + x2;
            if (rem !== 0 && result > 10) {
                return x1 + (10 - rem);
            } else if(rem > 0 && result < 0) { 
                return x1 - rem;
            }
            return x1 + x2;
        };
        
        function isVunerable() { 
            return eatable !== null;
        };
        
        function isDangerous() {
            return eaten === null;
        };

        function isHidden() { 
            return eatable === null && eaten !== null;
        };
        
        function getRandomDirection() {
            var moves = (direction === LEFT || direction === RIGHT) 
                ? [UP, DOWN] : [LEFT, RIGHT];
            return moves[Math.floor(Math.random() * 2)];
        };

        function getAvailableDirections(pos) {
            var dirs = [UP, DOWN, LEFT, RIGHT],
                result = [],
                i, d, npos;

            for (i = 0; i < dirs.length; i += 1) {
                d = dirs[i];
                npos = getNewCoord(d, pos);
                if (map.isFloorSpace({
                    "y": pointToCoord(nextSquare(npos.y, d)),
                    "x": pointToCoord(nextSquare(npos.x, d))
                })) {
                    result.push(d);
                }
            }

            return result;
        };

        function getEscapeDirection(pos) {
            var available = getAvailableDirections(pos),
                opposite = oppositeDirection(direction),
                choices, idx;

            if (available.length === 0) {
                return opposite;
            }

            choices = available.filter(function (d) { return d !== opposite; });
            if (choices.length === 0) {
                choices = available;
            }

            idx = Math.floor(Math.random() * choices.length);
            return choices[idx];
        };
        
        function reset(startPosition) {
            eaten = null;
            eatable = null;
            position = startPosition ? {"x": startPosition.x, "y": startPosition.y} : {"x": 220, "y": 70};
            direction = getRandomDirection();
            due = getRandomDirection();
        };
        
        function onWholeSquare(x) {
            return x % 10 === 0;
        };
        
        function oppositeDirection(dir) { 
            return dir === LEFT && RIGHT ||
                dir === RIGHT && LEFT ||
                dir === UP && DOWN || UP;
        };

        function makeEatable() {
            direction = oppositeDirection(direction);
            eatable = game.getTick();
        };

        function eat() { 
            eatable = null;
            eaten = game.getTick();
        };

        function pointToCoord(x) {
            return Math.round(x / 10);
        };

        function nextSquare(x, dir) {
            var rem = x % 10;
            if (rem === 0) { 
                return x; 
            } else if (dir === RIGHT || dir === DOWN) { 
                return x + (10 - rem);
            } else {
                return x - rem;
            }
        };

        function onGridSquare(pos) {
            return onWholeSquare(pos.y) && onWholeSquare(pos.x);
        };

        function secondsAgo(tick) { 
            return (game.getTick() - tick) / Pacman.FPS;
        };

        function getColour() { 
            if (eatable) { 
                if (secondsAgo(eatable) > 5) { 
                    return game.getTick() % 20 > 10 ? "#FFFFFF" : "#0000BB";
                } else { 
                    return "#0000BB";
                }
            } else if(eaten) { 
                return "#222";
            } 
            return colour;
        };

        function draw(ctx) {
    
            var s    = map.blockSize, 
                top  = (position.y/10) * s,
                left = (position.x/10) * s;
        
            if (eatable && secondsAgo(eatable) > 8) {
                eatable = null;
            }
            
            if (eaten && secondsAgo(eaten) > 3) { 
                eaten = null;
            }
            
            var tl = left + s;
            var base = top + s - 3;
            var inc = s / 10;

            var high = game.getTick() % 10 > 5 ? 3  : -3;
            var low  = game.getTick() % 10 > 5 ? -3 : 3;

            ctx.fillStyle = getColour();
            ctx.beginPath();

            ctx.moveTo(left, base);

            ctx.quadraticCurveTo(left, top, left + (s/2),  top);
            ctx.quadraticCurveTo(left + s, top, left+s,  base);
            
            // Wavy things at the bottom
            ctx.quadraticCurveTo(tl-(inc*1), base+high, tl - (inc * 2),  base);
            ctx.quadraticCurveTo(tl-(inc*3), base+low, tl - (inc * 4),  base);
            ctx.quadraticCurveTo(tl-(inc*5), base+high, tl - (inc * 6),  base);
            ctx.quadraticCurveTo(tl-(inc*7), base+low, tl - (inc * 8),  base); 
            ctx.quadraticCurveTo(tl-(inc*9), base+high, tl - (inc * 10), base); 

            ctx.closePath();
            ctx.fill();

            ctx.beginPath();
            ctx.fillStyle = "#FFF";
            ctx.arc(left + 6,top + 6, s / 6, 0, 300, false);
            ctx.arc((left + s) - 6,top + 6, s / 6, 0, 300, false);
            ctx.closePath();
            ctx.fill();

            var f = s / 12;
            var off = {};
            off[RIGHT] = [f, 0];
            off[LEFT]  = [-f, 0];
            off[UP]    = [0, -f];
            off[DOWN]  = [0, f];

            ctx.beginPath();
            ctx.fillStyle = "#152029";
            ctx.arc(left+6+off[direction][0], top+6+off[direction][1], 
                    s / 15, 0, 300, false);
            ctx.arc((left+s)-6+off[direction][0], top+6+off[direction][1], 
                    s / 15, 0, 300, false);
            ctx.closePath();
            ctx.fill();

        };

        function pane(pos) {

            var mapW = Pacman.COLS * 10;

            if (pos.y === 100 && pos.x >= mapW && direction === RIGHT) {
                return {"y": 100, "x": -10};
            }
            
            if (pos.y === 100 && pos.x <= -10 && direction === LEFT) {
                return position = {"y": 100, "x": mapW};
            }

            return false;
        };
        
        function move(ctx) {
            
            var oldPos = position,
                onGrid = onGridSquare(position),
                npos   = null;
            
            if (due !== direction) {
                
                npos = getNewCoord(due, position);
                
                if (onGrid &&
                    map.isFloorSpace({
                        "y":pointToCoord(nextSquare(npos.y, due)),
                        "x":pointToCoord(nextSquare(npos.x, due))})) {
                    direction = due;
                } else {
                    npos = null;
                }
            }
            
            if (npos === null) {
                npos = getNewCoord(direction, position);
            }
            
            if (onGrid &&
                map.isWallSpace({
                    "y" : pointToCoord(nextSquare(npos.y, direction)),
                    "x" : pointToCoord(nextSquare(npos.x, direction))
                })) {

                direction = getEscapeDirection(position);
                due = direction;
                npos = getNewCoord(direction, position);
            }

            position = npos;        
            
            var tmp = pane(position);
            if (tmp) { 
                position = tmp;
            }
            
            due = getRandomDirection();
            
            return {
                "new" : position,
                "old" : oldPos
            };
        };
        
        return {
            "eat"         : eat,
            "isVunerable" : isVunerable,
            "isDangerous" : isDangerous,
            "makeEatable" : makeEatable,
            "reset"       : reset,
            "move"        : move,
            "draw"        : draw
        };
    };

    Pacman.User = function (game, map) {
        
        var position  = null,
            direction = null,
            eaten     = null,
            due       = null, 
            lives     = null,
            score     = 5,
            keyMap    = {};
        
        keyMap[KEY.ARROW_LEFT]  = LEFT;
        keyMap[KEY.ARROW_UP]    = UP;
        keyMap[KEY.ARROW_RIGHT] = RIGHT;
        keyMap[KEY.ARROW_DOWN]  = DOWN;

        function addScore(nScore) { 
            score += nScore;
            if (score >= 10000 && score - nScore < 10000) { 
                lives += 1;
            }
        };

        function theScore() { 
            return score;
        };

        function loseLife() { 
            lives -= 1;
        };

        function getLives() {
            return lives;
        };

        function initUser() {
            score = 0;
            lives = 3;
            newLevel();
        }
        
        function newLevel() {
            resetPosition();
            eaten = 0;
        };
        
        function resetPosition() {
            position = {"x": 10, "y": 160};
            direction = LEFT;
            due = LEFT;
        };
        
        function reset() {
            initUser();
            resetPosition();
        };        
        
        function keyDown(e) {
            if (typeof keyMap[e.keyCode] !== "undefined") { 
                due = keyMap[e.keyCode];
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
            return true;
        };

        function getNewCoord(dir, current) {   
            return {
                "x": current.x + (dir === LEFT && -2 || dir === RIGHT && 2 || 0),
                "y": current.y + (dir === DOWN && 2 || dir === UP    && -2 || 0)
            };
        };

        function onWholeSquare(x) {
            return x % 10 === 0;
        };

        function pointToCoord(x) {
            return Math.round(x/10);
        };
        
        function nextSquare(x, dir) {
            var rem = x % 10;
            if (rem === 0) { 
                return x; 
            } else if (dir === RIGHT || dir === DOWN) { 
                return x + (10 - rem);
            } else {
                return x - rem;
            }
        };

        function next(pos, dir) {
            return {
                "y" : pointToCoord(nextSquare(pos.y, dir)),
                "x" : pointToCoord(nextSquare(pos.x, dir)),
            };                               
        };

        function onGridSquare(pos) {
            return onWholeSquare(pos.y) && onWholeSquare(pos.x);
        };

        function isOnSamePlane(due, dir) { 
            return ((due === LEFT || due === RIGHT) && 
                    (dir === LEFT || dir === RIGHT)) || 
                ((due === UP || due === DOWN) && 
                (dir === UP || dir === DOWN));
        };

        function move(ctx) {
            
            var npos        = null, 
                nextWhole   = null, 
                oldPosition = position,
                block       = null;
            
            if (due !== direction) {
                npos = getNewCoord(due, position);
                
                if (isOnSamePlane(due, direction) || 
                    (onGridSquare(position) && 
                    map.isFloorSpace(next(npos, due)))) {
                    direction = due;
                } else {
                    npos = null;
                }
            }

            if (npos === null) {
                npos = getNewCoord(direction, position);
            }
            
            if (onGridSquare(position) && map.isWallSpace(next(npos, direction))) {
                direction = NONE;
            }

            if (direction === NONE) {
                return {"new" : position, "old" : position};
            }
            
            var mapW = Pacman.COLS * 10;

            if (npos.y === 100 && npos.x >= mapW && direction === RIGHT) {
                npos = {"y": 100, "x": -10};
            }
            
            if (npos.y === 100 && npos.x <= -12 && direction === LEFT) {
                npos = {"y": 100, "x": mapW};
            }
            
            position = npos;        
            nextWhole = next(position, direction);
            
            block = map.block(nextWhole);        
            
            if ((isMidSquare(position.y) || isMidSquare(position.x)) &&
                (block === Pacman.BISCUIT || block === Pacman.PILL)) {
                
                map.setBlock(nextWhole, Pacman.EMPTY);           
                addScore((block === Pacman.BISCUIT) ? 10 : 50);
                eaten += 1;

                if (map.getRemainingEatables() === 0) {
                    game.completedLevel();
                }
                
                if (block === Pacman.PILL) { 
                    game.eatenPill();
                }
            }   
                    
            return {
                "new" : position,
                "old" : oldPosition
            };
        };

        function isMidSquare(x) { 
            var rem = x % 10;
            return rem > 3 || rem < 7;
        };

        function calcAngle(dir, pos) { 
            if (dir == RIGHT && (pos.x % 10 < 5)) {
                return {"start":0.25, "end":1.75, "direction": false};
            } else if (dir === DOWN && (pos.y % 10 < 5)) { 
                return {"start":0.75, "end":2.25, "direction": false};
            } else if (dir === UP && (pos.y % 10 < 5)) { 
                return {"start":1.25, "end":1.75, "direction": true};
            } else if (dir === LEFT && (pos.x % 10 < 5)) {             
                return {"start":0.75, "end":1.25, "direction": true};
            }
            return {"start":0, "end":2, "direction": false};
        };

        function drawDead(ctx, amount) { 

            var size = map.blockSize, 
                half = size / 2;

            if (amount >= 1) { 
                return;
            }

            ctx.fillStyle = "#FFFF00";
            ctx.beginPath();        
            ctx.moveTo(((position.x/10) * size) + half, 
                    ((position.y/10) * size) + half);
            
            ctx.arc(((position.x/10) * size) + half, 
                    ((position.y/10) * size) + half,
                    half, 0, Math.PI * 2 * amount, true); 
            
            ctx.fill();    
        };

        function draw(ctx) { 

            var s     = map.blockSize, 
                angle = calcAngle(direction, position);

            ctx.fillStyle = "#FFFF00";

            ctx.beginPath();        

            ctx.moveTo(((position.x/10) * s) + s / 2,
                    ((position.y/10) * s) + s / 2);
            
            ctx.arc(((position.x/10) * s) + s / 2,
                    ((position.y/10) * s) + s / 2,
                    s / 2, Math.PI * angle.start, 
                    Math.PI * angle.end, angle.direction); 
            
            ctx.fill();    
        };
        
        initUser();

        return {
            "draw"          : draw,
            "drawDead"      : drawDead,
            "loseLife"      : loseLife,
            "getLives"      : getLives,
            "score"         : score,
            "addScore"      : addScore,
            "theScore"      : theScore,
            "keyDown"       : keyDown,
            "move"          : move,
            "newLevel"      : newLevel,
            "reset"         : reset,
            "resetPosition" : resetPosition
        };
    };

    Pacman.Map = function (size) {
        let height    = null, 
            width     = null, 
            blockSize = size,
            pillSize  = 0,
            remainingEatables = 0,
            map       = null;
        
        function withinBounds(y, x) {
            return y >= 0 && y < height && x >= 0 && x < width;
        }
        
        function isWall(pos) {
            return withinBounds(pos.y, pos.x) && map[pos.y][pos.x] === Pacman.WALL;
        }
        
        function isFloorSpace(pos) {
            if (!withinBounds(pos.y, pos.x)) {
                return false;
            }
            let peice = map[pos.y][pos.x];

            return peice === Pacman.EMPTY || 
                peice === Pacman.BISCUIT ||
                peice === Pacman.PILL;
        }
        
        function drawWall(ctx) {

            let i, j, p, line;
            
            ctx.strokeStyle = "#00FF00";
            ctx.lineWidth   = 5;
            ctx.lineCap     = "round";
            
            for (i = 0; i < Pacman.WALLS.length; i += 1) {
                line = Pacman.WALLS[i];
                ctx.beginPath();

                for (j = 0; j < line.length; j += 1) {
                    p = line[j];
                    
                    if (p.move) {
                        ctx.moveTo(p.move[0] * blockSize, p.move[1] * blockSize);
                    } else if (p.line) {
                        ctx.lineTo(p.line[0] * blockSize, p.line[1] * blockSize);
                    } else if (p.curve) {
                        ctx.quadraticCurveTo(p.curve[0] * blockSize, 
                                            p.curve[1] * blockSize,
                                            p.curve[2] * blockSize, 
                                            p.curve[3] * blockSize);   
                    }
                }
                ctx.stroke();
            }
        }
        
        function reset() {       
            map    = Pacman.MAP.clone();
            height = map.length;
            width  = map[0].length;        

            remainingEatables = 0;
            for (let i = 0; i < height; i += 1) {
                for (let j = 0; j < width; j += 1) {
                    if (map[i][j] === Pacman.BISCUIT || map[i][j] === Pacman.PILL) {
                        remainingEatables += 1;
                    }
                }
            }
        };

        function block(pos) {
            return map[pos.y][pos.x];
        };
        
        function setBlock(pos, type) {
            let current = map[pos.y][pos.x];
            if ((current === Pacman.BISCUIT || current === Pacman.PILL) && type === Pacman.EMPTY) {
                remainingEatables = Math.max(0, remainingEatables - 1);
            } else if (current === Pacman.EMPTY && (type === Pacman.BISCUIT || type === Pacman.PILL)) {
                remainingEatables += 1;
            }

            map[pos.y][pos.x] = type;
        };

        function getRemainingEatables() {
            return remainingEatables;
        };

        function drawPills(ctx) { 
            if (++pillSize > 30) {
                pillSize = 0;
            }
            
            for (let i = 0; i < height; i += 1) {
                for (let j = 0; j < width; j += 1) {
                    if (map[i][j] === Pacman.PILL) {
                        ctx.beginPath();

                        ctx.fillStyle = "#152029";
                        ctx.fillRect((j * blockSize), (i * blockSize), 
                                    blockSize, blockSize);

                        ctx.fillStyle = "#FFF";
                        ctx.arc((j * blockSize) + blockSize / 2,
                                (i * blockSize) + blockSize / 2,
                                Math.abs(5 - (pillSize/3)), 
                                0, 
                                Math.PI * 2, false); 
                        ctx.fill();
                        ctx.closePath();
                    }
                }
            }
        };
        
        function draw(ctx) {
            
            let i, j, size = blockSize;

            ctx.fillStyle = "#152029";
            ctx.fillRect(0, -Pacman.TOP_PADDING, width * size, (height * size) + Pacman.TOP_PADDING);

            drawWall(ctx);
            
            for (i = 0; i < height; i += 1) {
                for (j = 0; j < width; j += 1) {
                    drawBlock(i, j, ctx);
                }
            }
        };
        
        function drawBlock(y, x, ctx) {

            let layout = map[y][x];

            if (layout === Pacman.PILL) {
                return;
            }

            ctx.beginPath();
            
            if (layout === Pacman.EMPTY || layout === Pacman.BLOCK || 
                layout === Pacman.BISCUIT) {
                
                ctx.fillStyle = "#152029";
                ctx.fillRect((x * blockSize), (y * blockSize), 
                            blockSize, blockSize);

                if (layout === Pacman.BISCUIT) {
                    ctx.fillStyle = "#FFF";
                    ctx.fillRect((x * blockSize) + (blockSize / 2.5), 
                                (y * blockSize) + (blockSize / 2.5), 
                                blockSize / 6, blockSize / 6);
                }
            }
            ctx.closePath();	 
        };

        reset();
        
        return {
            "draw"         : draw,
            "drawBlock"    : drawBlock,
            "drawPills"    : drawPills,
            "block"        : block,
            "setBlock"     : setBlock,
            "getRemainingEatables": getRemainingEatables,
            "reset"        : reset,
            "isWallSpace"  : isWall,
            "isFloorSpace" : isFloorSpace,
            "height"       : height,
            "width"        : width,
            "blockSize"    : blockSize
        };
    };

    let PACMAN = (function () {

        var state        = WAITING,
            ghosts       = [],
            ghostSpecs   = ["#00FFDE", "#FF0000", "#FFB8DE", "#FFB847"],
            ghostSpawnBase = {"x": 220, "y": 10},
            ghostSpawnStep = 10,
            eatenCount   = 0,
            level        = 0,
            tick         = 0,
            ghostPos, userPos, 
            stateChanged = true,
            timerStart   = null,
            lastTime     = 0,
            ctx          = null,
            timer        = null,
            canvasEl     = null,
            map          = null,
            user         = null,
            stored       = null,
            gestureStart = null;

        function inMazeArea(canvasY) {
            if (!map) {
                return false;
            }
            const mazeTop = Pacman.TOP_PADDING;
            const mazeBottom = Pacman.TOP_PADDING + (map.height * map.blockSize);
            return canvasY >= mazeTop && canvasY <= mazeBottom;
        }

        function getCanvasPoint(clientX, clientY) {
            const rect = canvasEl.getBoundingClientRect();
            return {
                x: clientX - rect.left,
                y: clientY - rect.top
            };
        }

        function sendDirection(dir) {
            let keyCode;
            if (dir === LEFT) {
                keyCode = KEY.ARROW_LEFT;
            } else if (dir === RIGHT) {
                keyCode = KEY.ARROW_RIGHT;
            } else if (dir === UP) {
                keyCode = KEY.ARROW_UP;
            } else if (dir === DOWN) {
                keyCode = KEY.ARROW_DOWN;
            } else {
                return;
            }

            user.keyDown({
                keyCode,
                preventDefault: function () {},
                stopPropagation: function () {}
            });
        }

        function handleGestureEnd(endClientX, endClientY, endTime) {
            if (!gestureStart || !canvasEl) {
                return;
            }

            const dx = endClientX - gestureStart.clientX;
            const dy = endClientY - gestureStart.clientY;
            const dist = Math.sqrt((dx * dx) + (dy * dy));

            const tapMaxDist = map ? (map.blockSize * 0.35) : 12;
            const swipeMinDist = map ? (map.blockSize * 0.6) : 24;

            // Tap: start game (only when tapping inside maze)
            if (dist <= tapMaxDist) {
                if (state === WAITING) {
                    startNewGame();
                }
                gestureStart = null;
                return;
            }

            // Swipe: change direction
            if (dist < swipeMinDist) {
                gestureStart = null;
                return;
            }

            if (state !== PAUSE) {
                if (Math.abs(dx) > Math.abs(dy)) {
                    sendDirection(dx > 0 ? RIGHT : LEFT);
                } else {
                    sendDirection(dy > 0 ? DOWN : UP);
                }
            }

            gestureStart = null;
        }

        function onPointerDown(e) {
            if (!canvasEl || !e) {
                return;
            }
            if (e.pointerType && e.pointerType !== 'touch') {
                return;
            }

            const p = getCanvasPoint(e.clientX, e.clientY);
            if (!inMazeArea(p.y)) {
                return;
            }

            gestureStart = { clientX: e.clientX, clientY: e.clientY, time: Date.now() };
            if (typeof e.preventDefault === 'function') {
                e.preventDefault();
            }
        }

        function onPointerUp(e) {
            if (!gestureStart || !canvasEl || !e) {
                return;
            }
            if (e.pointerType && e.pointerType !== 'touch') {
                gestureStart = null;
                return;
            }
            if (typeof e.preventDefault === 'function') {
                e.preventDefault();
            }
            handleGestureEnd(e.clientX, e.clientY, Date.now());
        }

        function onTouchStart(e) {
            if (!canvasEl || !e || !e.touches || e.touches.length !== 1) {
                return;
            }
            const t = e.touches[0];
            const p = getCanvasPoint(t.clientX, t.clientY);
            if (!inMazeArea(p.y)) {
                return;
            }
            gestureStart = { clientX: t.clientX, clientY: t.clientY, time: Date.now() };
            e.preventDefault();
        }

        function onTouchEnd(e) {
            if (!gestureStart || !canvasEl || !e || !e.changedTouches || e.changedTouches.length < 1) {
                gestureStart = null;
                return;
            }
            const t = e.changedTouches[0];
            e.preventDefault();
            handleGestureEnd(t.clientX, t.clientY, Date.now());
        }

        function getTick() { 
            return tick;
        };

        function drawScore(text, position) {
            ctx.fillStyle = "#FFFFFF";
            ctx.font      = "12px Satoshi";
            ctx.fillText(text, 
                        (position["new"]["x"] / 10) * map.blockSize, 
                        ((position["new"]["y"] + 5) / 10) * map.blockSize);
        }
        
        function dialog(text) {
            ctx.fillStyle = "#FFFFFF";
            ctx.font      = "24px Satoshi";
            let width = ctx.measureText(text).width,
                x     = ((map.width * map.blockSize) - width) / 2;        
            // draw in the padding area above the maze
            ctx.fillText(text, x, -Pacman.TOP_PADDING / 3);
        }
        
        function startLevel() {        
            user.resetPosition();
            for (var i = 0; i < ghosts.length; i += 1) { 
                ghosts[i].reset({
                    "x": ghostSpawnBase.x + (i * ghostSpawnStep),
                    "y": ghostSpawnBase.y
                });
            }
            lastTime = null;
            map.draw(ctx);
            timerStart = tick;
            setState(COUNTDOWN);
        }    

        function startNewGame() {
            setState(WAITING);
            level = 1;
            user.reset();
            map.reset();
            map.draw(ctx);
            startLevel();
        }

        function keyDown(e) {
            if (e.keyCode === KEY.N) {
                startNewGame();
            } else if (e.keyCode === KEY.P && state === PAUSE) {
                map.draw(ctx);
                setState(stored);
            } else if (e.keyCode === KEY.P) {
                stored = state;
                setState(PAUSE);
                map.draw(ctx);
                dialog("Paused");
            } else if (state !== PAUSE) {   
                return user.keyDown(e);
            }
            return true;
        }    

        function loseLife() {        
            setState(WAITING);
            user.loseLife();
            if (user.getLives() > 0) {
                startLevel();
            }
        }

        function setState(nState) { 
            state = nState;
            stateChanged = true;
        };
        
        function collided(user, ghost) {
            return (Math.sqrt(Math.pow(ghost.x - user.x, 2) + 
                            Math.pow(ghost.y - user.y, 2))) < 10;
        };

        function drawFooter() {
            
            let topLeft  = (map.height * map.blockSize),
                textBase = topLeft + 17;
            
            ctx.fillStyle = "#152029";
            ctx.fillRect(0, topLeft, (map.width * map.blockSize), 30);

                const footerWidth = map.width * map.blockSize;
                const lifeRadius = map.blockSize / 2;
                const lifeSpacing = 25;
                const lifeOffsetX = 10;
                const lifeOffsetY = 4;
                const lifeCount = user.getLives();
                const livesGroupWidth = (lifeCount > 0) ? ((lifeSpacing * (lifeCount - 1)) + (lifeRadius * 2)) : 0;
                const lifeBaseX = ((footerWidth - livesGroupWidth) / 2) + lifeOffsetX;
                const lifeCenterY = (textBase - lifeRadius) + lifeOffsetY;

                ctx.fillStyle = "#FFFFFF";

            for (let i = 0, len = lifeCount; i < len; i++) {
                ctx.beginPath();
                ctx.moveTo(lifeBaseX + (lifeSpacing * i) + map.blockSize / 2,
                    lifeCenterY);
                
                ctx.arc(lifeBaseX + (lifeSpacing * i) + map.blockSize / 2,
                    lifeCenterY,
                    lifeRadius, Math.PI * 0.25, Math.PI * 1.75, false);
                ctx.fill();
            }

            ctx.fillStyle = "#00FF00";
            ctx.font = "bold 16px Satoshi";
            // ctx.fillText("♪", 10, textBase);
            // ctx.fillText("s", 10, textBase);

            ctx.fillStyle = "#FFFFFF";
            ctx.font      = "14px Satoshi";
            ctx.fillText("Score: " + user.theScore(), 10, textBase);

            const levelText = "Level: " + level;
            const levelPaddingRight = 10;
            const levelX = footerWidth - levelPaddingRight - ctx.measureText(levelText).width;
            ctx.fillText(levelText, levelX, textBase);
        }

        function redrawBlock(pos) {
            map.drawBlock(Math.floor(pos.y/10), Math.floor(pos.x/10), ctx);
            map.drawBlock(Math.ceil(pos.y/10), Math.ceil(pos.x/10), ctx);
        }

        function mainDraw() { 

            let diff, u, i, len, nScore;
            
            ghostPos = [];

            for (i = 0, len = ghosts.length; i < len; i += 1) {
                ghostPos.push(ghosts[i].move(ctx));
            }
            u = user.move(ctx);
            
            for (i = 0, len = ghosts.length; i < len; i += 1) {
                redrawBlock(ghostPos[i].old);
            }
            redrawBlock(u.old);
            
            for (i = 0, len = ghosts.length; i < len; i += 1) {
                ghosts[i].draw(ctx);
            }                     
            user.draw(ctx);
            
            userPos = u["new"];
            
            for (i = 0, len = ghosts.length; i < len; i += 1) {
                if (collided(userPos, ghostPos[i]["new"])) {
                    if (ghosts[i].isVunerable()) { 
                        ghosts[i].eat();
                        eatenCount += 1;
                        nScore = eatenCount * 50;
                        drawScore(nScore, ghostPos[i]);
                        user.addScore(nScore);                    
                        setState(EATEN_PAUSE);
                        timerStart = tick;
                    } else if (ghosts[i].isDangerous()) {
                        setState(DYING);
                        timerStart = tick;
                    }
                }
            }                             
        };

        function mainLoop() {

            var diff;

            if (state !== PAUSE) { 
                ++tick;
            }

            map.drawPills(ctx);

            if (state === PLAYING) {
                mainDraw();
            } else if (state === WAITING && stateChanged) {            
                stateChanged = false;
                map.draw(ctx);

                if (isTouchDevice.value === true) {
                    dialog("Tap the maze to start the game");            
                } else {
                    dialog("Press N to start a New game");            
                }
            } else if (state === EATEN_PAUSE && 
                    (tick - timerStart) > (Pacman.FPS / 3)) {
                map.draw(ctx);
                setState(PLAYING);
            } else if (state === DYING) {
                if (tick - timerStart > (Pacman.FPS * 2)) { 
                    loseLife();
                } else { 
                    redrawBlock(userPos);

                    for (let i = 0, len = ghosts.length; i < len; i += 1) {
                        redrawBlock(ghostPos[i].old);
                        ghostPos.push(ghosts[i].draw(ctx));
                    }                                   
                    user.drawDead(ctx, (tick - timerStart) / (Pacman.FPS * 2));
                }
            } else if (state === COUNTDOWN) {
                
                diff = 3 + Math.floor((timerStart - tick) / Pacman.FPS);
                
                if (diff === 0) {
                    map.draw(ctx);
                    setState(PLAYING);
                } else {
                    if (diff !== lastTime) { 
                        lastTime = diff;
                        map.draw(ctx);
                        dialog("Starting in: " + diff);
                    }
                }
            } 

            drawFooter();
        }

        function eatenPill() {
            timerStart = tick;
            eatenCount = 0;

            for (let i = 0; i < ghosts.length; i += 1) {
                ghosts[i].makeEatable(ctx);
            }        
        };
        
        function completedLevel() {
            level += 1;
            map.reset();
            user.newLevel();
            startLevel();
        };

        function keyPress(e) { 
            if (state !== WAITING && state !== PAUSE) { 
                e.preventDefault();
                e.stopPropagation();
            }
        };
        
        function init(wrapper) {
            
            var i, len, ghost,
                cols      = Pacman.COLS,
                rows      = Pacman.ROWS,
                blockSize = wrapper.offsetWidth / cols,
                canvas    = document.createElement("canvas");
            
            canvas.setAttribute("width", (blockSize * cols) + "px");
            canvas.setAttribute("height", (blockSize * rows) + 30 + Pacman.TOP_PADDING + "px");

            wrapper.appendChild(canvas);

            canvasEl = canvas;

            ctx  = canvas.getContext('2d');
            ctx.translate(0, Pacman.TOP_PADDING);

            map   = new Pacman.Map(blockSize);
            user  = new Pacman.User({ 
                "completedLevel" : completedLevel, 
                "eatenPill"      : eatenPill 
            }, map);

            if (!DEBUG_DISABLE_GHOSTS) {
                for (i = 0, len = ghostSpecs.length; i < len; i += 1) {
                    ghost = new Pacman.Ghost({"getTick":getTick}, map, ghostSpecs[i]);
                    ghosts.push(ghost);
                }
            }
            
            map.draw(ctx);

            loaded()
        };
            
        function loaded() {
            if (isTouchDevice.value === true) {
                dialog("Tap the maze to start the game");            
            } else {
                dialog("Press N to Start");            
            }

            if (canvasEl) {
                // Disable browser scrolling/zooming gestures over the game
                canvasEl.style.touchAction = 'none';

                canvasEl.addEventListener('pointerdown', onPointerDown, { passive: false });
                canvasEl.addEventListener('pointerup', onPointerUp, { passive: false });

                // Fallback for browsers without Pointer Events
                canvasEl.addEventListener('touchstart', onTouchStart, { passive: false });
                canvasEl.addEventListener('touchend', onTouchEnd, { passive: false });
            }
            
            document.addEventListener("keydown", keyDown, true);
            document.addEventListener("keypress", keyPress, true); 
            
            timer = window.setInterval(mainLoop, 1000 / Pacman.FPS);
        };
        
        return {
            "init" : init
        };
        
    }());

    /* Human readable keyCode index */
    let KEY = {'BACKSPACE': 8, 'TAB': 9, 'NUM_PAD_CLEAR': 12, 'ENTER': 13, 'SHIFT': 16, 'CTRL': 17, 'ALT': 18, 'PAUSE': 19, 'CAPS_LOCK': 20, 'ESCAPE': 27, 'SPACEBAR': 32, 'PAGE_UP': 33, 'PAGE_DOWN': 34, 'END': 35, 'HOME': 36, 'ARROW_LEFT': 37, 'ARROW_UP': 38, 'ARROW_RIGHT': 39, 'ARROW_DOWN': 40, 'PRINT_SCREEN': 44, 'INSERT': 45, 'DELETE': 46, 'SEMICOLON': 59, 'WINDOWS_LEFT': 91, 'WINDOWS_RIGHT': 92, 'SELECT': 93, 'NUM_PAD_ASTERISK': 106, 'NUM_PAD_PLUS_SIGN': 107, 'NUM_PAD_HYPHEN-MINUS': 109, 'NUM_PAD_FULL_STOP': 110, 'NUM_PAD_SOLIDUS': 111, 'NUM_LOCK': 144, 'SCROLL_LOCK': 145, 'SEMICOLON': 186, 'EQUALS_SIGN': 187, 'COMMA': 188, 'HYPHEN-MINUS': 189, 'FULL_STOP': 190, 'SOLIDUS': 191, 'GRAVE_ACCENT': 192, 'LEFT_SQUARE_BRACKET': 219, 'REVERSE_SOLIDUS': 220, 'RIGHT_SQUARE_BRACKET': 221, 'APOSTROPHE': 222};

    (function () {
        /* 0 - 9 */
        for (var i = 48; i <= 57; i++) {
            KEY['' + (i - 48)] = i;
        }
        /* A - Z */
        for (i = 65; i <= 90; i++) {
            KEY['' + String.fromCharCode(i)] = i;
        }
        /* NUM_PAD_0 - NUM_PAD_9 */
        for (i = 96; i <= 105; i++) {
            KEY['NUM_PAD_' + (i - 96)] = i;
        }
        /* F1 - F12 */
        for (i = 112; i <= 123; i++) {
            KEY['F' + (i - 112 + 1)] = i;
        }
    })();

    Pacman.WALL    = 0;
    Pacman.BISCUIT = 1;
    Pacman.EMPTY   = 2;
    Pacman.BLOCK   = 3;
    Pacman.PILL    = 4;
     
    Pacman.MAP = [
        [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
        [0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0],
        [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
        [0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0],
        [0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0],
        [0, 1, 0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 1, 1, 1, 1, 0],
        [0, 1, 0, 0, 0, 4, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 4, 0, 0, 0, 1, 0],
        [0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0],
        [0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0],
        [0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0],
        [0, 1, 1, 1, 1, 4, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 4, 0, 0, 0, 1, 0],
        [0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0],
        [0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0],
        [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
        [0, 1, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0],
        [0, 2, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
        [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    ];

    // Zentrale Grid-Dimensionen (für Canvas/Wrap-Around etc.)
    Pacman.COLS = Pacman.MAP[0].length;
    Pacman.ROWS = Pacman.MAP.length;

    Pacman.WALLS = [
        [{"move": [2.5, 2.5]}, {"line": [8.5, 2.5]}],

        [{"move": [10.5, 2.5]}, {"line": [13.5, 2.5]}],

        [{"move": [15.5, 2.5]}, {"line": [19.5, 2.5]}],

        [{"move": [21.5, 2.5]}, {"line": [25.5, 2.5]}],
        
        [{"move": [2.5, 15.5]}, {"line": [7.5, 15.5]}],

        [{"move": [9.5, 15.5]}, {"line": [13.5, 15.5]}],

        [{"move": [15.5, 15.5]}, {"line": [19.5, 15.5]}],

        [{"move": [21.5, 15.5]}, {"line": [25.5, 15.5]}],

        [
            {"move": [24, 4.5]}, {"line": [25, 4.5]},
            {"curve": [25.5, 4.5, 25.5, 5]}, 
            {"curve": [25.5, 5.5, 25, 5.5]}, {"line": [24, 5.5]},
            {"curve": [23.5, 5.5, 23.5, 5]}, 
            {"curve": [23.5, 4.5, 24, 4.5]}
        ],

        [
            {"move": [7, 4.5]}, {"line": [8, 4.5]},
            {"curve": [8.5, 4.5, 8.5, 5]}, 
            {"curve": [8.5, 5.5, 8, 5.5]}, {"line": [7, 5.5]},
            {"curve": [6.5, 5.5, 6.5, 5]}, 
            {"curve": [6.5, 4.5, 7, 4.5]}
        ],

        [
            {"move": [3, 12.5]}, {"line": [4, 12.5]},
            {"curve": [4.5, 12.5, 4.5, 13]}, 
            {"curve": [4.5, 13.5, 4, 13.5]}, {"line": [3, 13.5]},
            {"curve": [2.5, 13.5, 2.5, 13]}, 
            {"curve": [2.5, 12.5, 3, 12.5]}
        ],

        [
            {"move": [20, 12.5]}, {"line": [21, 12.5]},
            {"curve": [21.5, 12.5, 21.5, 13]}, 
            {"curve": [21.5, 13.5, 21, 13.5]}, {"line": [20, 13.5]},
            {"curve": [19.5, 13.5, 19.5, 13]}, 
            {"curve": [19.5, 12.5, 20, 12.5]}
        ],

        // frame
        [
            {"move": [1, 0.5]}, {"line": [27, 0.5]},
            {"curve": [27.5, 0.5, 27.5, 1]}, {"line": [27.5, 17]},
            {"curve": [27.5, 17.5, 27, 17.5]}, {"line": [1, 17.5]},
            {"curve": [0.5, 17.5, 0.5, 17]}, {"line": [0.5, 1]},
            {"curve": [0.5, 0.5, 1, 0.5]}, 
        ],

        // first "4"
        [
            {"move": [3, 4.5]}, {"line": [4, 4.5]},
            {"curve": [4.5, 4.5, 4.5, 5]}, {"line": [4.5, 8]},
            {"curve": [4.5, 8.5, 5, 8.5]}, {"line": [6, 8.5]},
            {"curve": [6.5, 8.5, 6.5, 8]}, {"line": [6.5, 8]},
            {"curve": [6.5, 7.5, 7, 7.5]}, {"line": [8, 7.5]},
            {"curve": [8.5, 7.5, 8.5, 8]}, {"line": [8.5, 13]},
            {"curve": [8.5, 13.5, 8, 13.5]}, {"line": [7, 13.5]},
            {"curve": [6.5, 13.5, 6.5, 13]}, {"line": [6.5, 11]},
            {"curve": [6.5, 10.5, 6, 10.5]}, {"line": [3, 10.5]},
            {"curve": [2.5, 10.5, 2.5, 10]}, {"line": [2.5, 5]},
            {"curve": [2.5, 4.5, 3, 4.5]},
        ],

        // "0" outer
        [
            {"move": [17.5, 13]}, {"line": [17.5, 5]},
            {"curve": [17.5, 4.5, 17, 4.5]}, {"line": [11, 4.5]},
            {"curve": [10.5, 4.5, 10.5, 5]}, {"line": [10.5, 13]},
            {"curve": [10.5, 13.5, 11, 13.5]}, {"line": [17, 13.5]},
            {"curve": [17.5, 13.5, 17.5, 13]},
        ],

        // "0" inner
        [
            {"move": [15.5, 11]}, {"line": [15.5, 7]},
            {"curve": [15.5, 6.5, 15, 6.5]}, {"line": [13, 6.5]},
            {"curve": [12.5, 6.5, 12.5, 7]}, {"line": [12.5, 11]},
            {"curve": [12.5, 11.5, 13, 11.5]}, {"line": [15, 11.5]},
            {"curve": [15.5, 11.5, 15.5, 11]},
        ],

        // second "4"
        [
            {"move": [20, 4.5]}, {"line": [21, 4.5]},
            {"curve": [21.5, 4.5, 21.5, 5]}, {"line": [21.5, 8]},
            {"curve": [21.5, 8.5, 22, 8.5]}, {"line": [23, 8.5]},
            {"curve": [23.5, 8.5, 23.5, 8]}, {"line": [23.5, 8]},
            {"curve": [23.5, 7.5, 24, 7.5]}, {"line": [25, 7.5]},
            {"curve": [25.5, 7.5, 25.5, 8]}, {"line": [25.5, 13]},
            {"curve": [25.5, 13.5, 25, 13.5]}, {"line": [24, 13.5]},
            {"curve": [23.5, 13.5, 23.5, 13]}, {"line": [23.5, 11]},
            {"curve": [23.5, 10.5, 23, 10.5]}, {"line": [20, 10.5]},
            {"curve": [19.5, 10.5, 19.5, 10]}, {"line": [19.5, 5]},
            {"curve": [19.5, 4.5, 20, 4.5]},
        ],
    ];

    Object.prototype.clone = function () {
        let i, newObj = (this instanceof Array) ? [] : {};
        for (i in this) {
            if (i === 'clone') {
                continue;
            }
            if (this[i] && typeof this[i] === "object") {
                newObj[i] = this[i].clone();
            } else {
                newObj[i] = this[i];
            }
        }
        return newObj;
    };

    onMounted(() => {
        const el = document.getElementById("pacman");

        PACMAN.init(el)
    })

</script>

<style scoped>
    #pacman {
        height: auto;
        /* min-width: 382px; */
        min-width: 340px;
        border-radius: 5px;
        margin: 40px auto;
        display: flex;
        justify-content: center;
    }

    #pacman canvas {
        touch-action: none;
        -webkit-user-select: none;
        user-select: none;
    }

    .pacman-container {
        p span:not(:last-child) {
            margin-right: 40px;
        }
    }
</style>